import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { GitCompareArrows } from "lucide-react";
import { api } from "@/lib/api";
import { CLS_STYLE } from "@/components/BlindBaselinePanel";

const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.map(fmt).join(" × ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};
const na = (v) => (v === null || v === undefined ? "N/A" : v);
const short = (h) => (h ? `${h.slice(0, 10)}…` : "—");

const GATE_STYLE = {
  READING_READY: "bg-emerald-100 text-emerald-800",
  READING_BLOCKED: "bg-red-100 text-red-800",
  RICONOSCIUTA: "bg-emerald-100 text-emerald-800",
  MANCANTE: "bg-red-100 text-red-800",
  NON_DIMOSTRATA: "bg-amber-100 text-amber-800",
};

/* ── meta bar ─────────────────────────────────────────────────── */
const Meta = ({ d }) => {
  const e = d.engine || {};
  return (
    <div className="text-[11px] grid md:grid-cols-2 gap-x-6 gap-y-0.5 border border-border p-2 bg-zinc-50" data-testid="p03-meta">
      <div>Motore P0.2: deepread <b>{e.p02?.deepread_version}</b> · commit <span className="font-mono">{short(e.p02?.git_commit)}</span></div>
      <div>Motore P0.3: deepread <b>{e.p03?.deepread_version}</b> · commit <span className="font-mono">{short(e.p03?.git_commit)}</span></div>
      <div>Output P0.2: <span className="font-mono" data-testid="p03-hash-p02">{short(e.p02_output_sha256)}</span> → P0.3: <span className="font-mono" data-testid="p03-hash-p03">{short(e.p03_output_sha256)}</span></div>
      <div>run_id: <span className="font-mono">{e.p03_run_id}</span> · cache_hit: <b data-testid="p03-cache-hit">{String(e.p03_cache?.cache_hit)}</b> · durata {e.p03_elapsed_s}s · config identica: <b>{String(e.config_identical)}</b></div>
      <div className="md:col-span-2 text-amber-800">{d.dev_regression_note}</div>
    </div>
  );
};

/* ── campi voce per voce ──────────────────────────────────────── */
const FieldDelta = ({ rows }) => (
  <table className="w-full text-[11px]" data-testid="p03-field-table">
    <thead><tr className="text-left text-muted-foreground">
      <th className="pr-2 py-1">Campo</th><th className="pr-2">P0.2 congelato</th><th className="pr-2">P0.3</th>
      <th className="pr-2">Ground truth</th><th className="pr-2">Evidenza P0.3</th><th>Δ</th>
    </tr></thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.key} className={`border-t border-border align-top ${r.changed ? "bg-amber-50/60" : ""}`} data-testid={`p03-field-${r.key}`}>
          <td className="pr-2 py-1 font-medium">{r.label}</td>
          <td className="pr-2 break-all">{fmt(r.p02.value)}{r.p02.unit ? ` ${r.p02.unit}` : ""} <Badge className={`text-[9px] ${CLS_STYLE[r.p02.status] || "bg-zinc-100"}`}>{r.p02.status}</Badge></td>
          <td className="pr-2 break-all">{fmt(r.p03.value)}{r.p03.unit ? ` ${r.p03.unit}` : ""} <Badge className={`text-[9px] ${CLS_STYLE[r.p03.status] || "bg-zinc-100"}`}>{r.p03.status}</Badge></td>
          <td className="pr-2">{r.gt_raw != null ? <span title={fmt(r.gt_raw)}>{fmt(r.gt_value ?? r.gt_raw)}</span> : <span className="text-muted-foreground">non censito</span>}</td>
          <td className="pr-2 max-w-[220px] text-muted-foreground break-all">{fmt(r.p03.evidence)}</td>
          <td>{r.changed ? "≠" : "="}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

/* ── transizioni + TP/FP/FN ───────────────────────────────────── */
const Transitions = ({ d }) => {
  const tr = d.items_transition || {};
  const t2 = d.tp_fp_fn?.p02 || {};
  const t3 = d.tp_fp_fn?.p03 || {};
  const cats = [...new Set([...Object.keys(t2), ...Object.keys(t3)])].sort();
  const Row = ({ r, icon, cls }) => (
    <div className={`text-[11px] py-0.5 border-b border-border/50 ${cls || ""}`}>
      {icon} <b>{r.category}</b> / {r.label}: {r.p02_classification || "—"} → <Badge className={`text-[9px] ${CLS_STYLE[r.p03_classification] || ""}`}>{r.p03_classification}</Badge>
      {" "}(P0.3={fmt(r.p03_value)}, GT={fmt(r.gt)})
    </div>
  );
  return (
    <div className="space-y-3" data-testid="p03-transitions">
      <table className="w-full text-[11px]" data-testid="p03-tpfpfn-table">
        <thead><tr className="text-left text-muted-foreground">
          <th className="pr-2 py-1">Categoria</th><th className="pr-2">TP</th><th className="pr-2">FP</th><th className="pr-2">FN</th>
          <th className="pr-2">Assenze conf.</th><th className="pr-2">Precision</th><th>Recall</th>
        </tr></thead>
        <tbody>
          {cats.map((c) => {
            const a = t2[c] || {}, b = t3[c] || {};
            return (
              <tr key={c} className="border-t border-border" data-testid={`p03-tpfpfn-${c}`}>
                <td className="pr-2 py-1 font-medium">{c}</td>
                <td className="pr-2">{a.tp ?? 0} → <b>{b.tp ?? 0}</b></td>
                <td className="pr-2">{a.fp ?? 0} → <b>{b.fp ?? 0}</b></td>
                <td className="pr-2">{a.fn ?? 0} → <b>{b.fn ?? 0}</b></td>
                <td className="pr-2">{a.tn_assenze ?? 0} → {b.tn_assenze ?? 0}</td>
                <td className="pr-2">{na(a.precision)} → <b>{na(b.precision)}</b></td>
                <td>{na(a.recall)} → <b>{na(b.recall)}</b></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-[10px] text-muted-foreground">{d.tp_fp_fn?.nota}</p>
      <div className="grid md:grid-cols-2 gap-3">
        <div className="border border-emerald-200 bg-emerald-50 p-2" data-testid="p03-improved">
          <h4 className="text-xs font-semibold text-emerald-900">Migliorate ({(tr.improved || []).length})</h4>
          {(tr.improved || []).map((r, i) => <Row key={i} r={r} icon="✅" />)}
        </div>
        <div className="border border-red-200 bg-red-50 p-2" data-testid="p03-new-errors">
          <h4 className="text-xs font-semibold text-red-900">Nuovi errori introdotti ({(tr.new_errors_introduced || []).length})</h4>
          {(tr.new_errors_introduced || []).map((r, i) => <Row key={i} r={r} icon="❌" />)}
          {(tr.new_errors_introduced || []).length === 0 && <p className="text-[11px]">nessuno</p>}
        </div>
        <div className="border border-amber-200 bg-amber-50 p-2 md:col-span-2" data-testid="p03-still-bad">
          <h4 className="text-xs font-semibold text-amber-900">Ancora errate ({(tr.unchanged_bad || []).length}) · invariate corrette/neutre: {tr.unchanged_ok_count}</h4>
          {(tr.unchanged_bad || []).map((r, i) => <Row key={i} r={r} icon="⚠️" />)}
        </div>
      </div>
      <div className="border border-border p-2" data-testid="p03-danger">
        <h4 className="text-xs font-semibold">Errori che rischierebbero il preventivo: P0.2 = {(d.errori_da_preventivo?.p02 || []).length} → P0.3 = {(d.errori_da_preventivo?.p03 || []).length}</h4>
        {(d.errori_da_preventivo?.p03 || []).map((x, i) => (
          <div key={i} className="text-[11px] py-0.5 border-b border-border/50">
            <Badge className={`text-[9px] mr-1 ${CLS_STYLE[x.classification]}`}>{x.classification}</Badge>
            {x.label}: P0.3 {fmt(x.quoteos_value)} vs GT {fmt(x.gt_value)} — {x.nota}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── gate + fasi produttive ───────────────────────────────────── */
const GateAndPhases = ({ d }) => {
  const rg = d.reading_gate_p03 || {};
  const ph = d.phases_p03 || {};
  return (
    <div className="space-y-3" data-testid="p03-gate-phases">
      <div className="border border-border p-2">
        <h4 className="text-xs font-semibold flex items-center gap-2">
          Gate tecnico di lettura <Badge className={`text-[10px] ${GATE_STYLE[rg.status] || "bg-zinc-100"}`} data-testid="p03-reading-gate-status">{rg.status || "—"}</Badge>
          <span className="text-[10px] text-muted-foreground">indipendente da PRICING ({fmt(d.pricing_gate_p03?.status || d.pricing_gate_p03?.definitive_quote_allowed)})</span>
        </h4>
        {(rg.blockers || []).map((b, i) => (
          <div key={i} className="text-[11px] py-0.5 border-b border-border/50" data-testid={`p03-blocker-${b.code}`}>
            <b>{b.code}</b>: {b.reason} → <span className="text-sky-800">{b.next_action}</span>
          </div>
        ))}
        {(rg.blockers || []).length === 0 && <p className="text-[11px] text-emerald-800">nessun blocco: lettura completa e coerente</p>}
        <p className="text-[10px] text-muted-foreground mt-1">{rg.note}</p>
      </div>
      <div className="border border-border p-2">
        <h4 className="text-xs font-semibold">Fasi produttive attese (da GT) vs riconosciute</h4>
        {(ph.attese_vs_riconosciute || []).map((r, i) => (
          <div key={i} className="text-[11px] py-0.5 border-b border-border/50" data-testid={`p03-phase-${r.operation}`}>
            <Badge className={`text-[9px] mr-1 ${GATE_STYLE[r.state] || "bg-zinc-100"}`}>{r.state}</Badge>
            <b>{r.operation}</b> — {r.gt_note}
            {r.phase && <span className="text-muted-foreground"> · evidenza: {r.phase.evidence_state} · tecnologie candidate: {(r.phase.candidate_technologies || []).join(", ")} · macchina decisa: {String(r.phase.machine_decided)}</span>}
          </div>
        ))}
      </div>
      <div className="border border-border p-2">
        <h4 className="text-xs font-semibold">Tutte le fasi P0.3 ({(ph.tutte_le_fasi_p03 || []).length})</h4>
        <table className="w-full text-[11px]" data-testid="p03-phases-table">
          <thead><tr className="text-left text-muted-foreground">
            <th className="pr-2 py-0.5">Operazione</th><th className="pr-2">Evidenza</th><th className="pr-2">Input mancanti</th><th className="pr-2">Prossima azione</th><th>Tecn. candidate</th>
          </tr></thead>
          <tbody>
            {(ph.tutte_le_fasi_p03 || []).map((p, i) => (
              <tr key={i} className="border-t border-border align-top">
                <td className="pr-2 py-0.5 font-medium">{p.operation}</td>
                <td className="pr-2"><Badge className={`text-[9px] ${CLS_STYLE[p.evidence_state] || "bg-zinc-100"}`}>{p.evidence_state}</Badge></td>
                <td className="pr-2 text-amber-800">{(p.missing_inputs || []).join(", ") || "—"}</td>
                <td className="pr-2">{p.next_action}</td>
                <td>{(p.candidate_technologies || []).join(", ") || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ── overlay P0.2 / P0.3 distinguibili ────────────────────────── */
const OverlayDelta = ({ d, caseId }) => {
  const [src, setSrc] = useState("p03");
  const overlay = useMemo(() => (src === "p03" ? d.overlay_p03 : d.overlay_p02) || {}, [src, d]);
  const [page, setPage] = useState(overlay.pages?.[0]?.page || 1);
  const pg = useMemo(() => (overlay.pages || []).find((p) => p.page === page) || {}, [overlay, page]);
  const [x0, y0, x1, y1] = pg.page_rect || [0, 0, 595, 842];
  const color = src === "p03" ? { h: "#dc2626", s: "#d97706", dl: "#16a34a", df: "#84cc16", b: "#7c3aed" } : { h: "#f87171", s: "#fbbf24", dl: "#86efac", df: "#d9f99d", b: "#c4b5fd" };
  const holes = (overlay.holes || []).filter((h) => h.page === page);
  const slots = (overlay.slots || []).filter((s) => s.page === page);
  const dims = (overlay.dimensions || []).filter((dd) => dd.page === page && !dd.discarded);
  const bends = (overlay.bends || []).filter((b) => b.page === page);
  const roll = overlay.rolling && overlay.rolling.page === page ? overlay.rolling : null;
  const box = (b, c, key, dash, w = 1.6) => b && b.length === 4 && (
    <rect key={key} x={b[0]} y={b[1]} width={Math.max(b[2] - b[0], 2)} height={Math.max(b[3] - b[1], 2)} fill="none" stroke={c} strokeWidth={w} strokeDasharray={dash} />);
  return (
    <div data-testid="p03-overlay">
      <div className="flex gap-2 items-center mb-1 flex-wrap">
        {[["p02", "Overlay P0.2 (congelato)"], ["p03", "Overlay P0.3 (nuovo parser)"]].map(([k, l]) => (
          <Button key={k} size="sm" variant={src === k ? "default" : "outline"} className="h-6 text-[11px]" onClick={() => setSrc(k)} data-testid={`p03-overlay-src-${k}`}>{l}</Button>
        ))}
        {(overlay.pages || []).length > 1 && (
          <select className="h-6 border border-border text-[11px] bg-background" value={page} onChange={(e) => setPage(Number(e.target.value))}>
            {(overlay.pages || []).map((p) => <option key={p.page} value={p.page}>pagina {p.page}</option>)}
          </select>
        )}
        <span className="text-[10px] text-muted-foreground">
          {src === "p03" ? "coordinate ESCLUSIVAMENTE dal parser P0.3 (mai dalla GT): pieno" : "coordinate dal raw P0.2 congelato: colori attenuati"}
          {" "}· quote associate/verdi · fori/rossi · asole/arancio · pieghe-anello/viola
        </span>
      </div>
      <div className="relative border border-border">
        <img src={`${api.defaults.baseURL}/calibration/cases/${caseId}/page-image/${page}`} alt="disegno" className="w-full block" data-testid="p03-overlay-image" />
        <svg className="absolute inset-0 w-full h-full" viewBox={`${x0} ${y0} ${x1 - x0} ${y1 - y0}`} preserveAspectRatio="none" style={{ pointerEvents: "none" }} data-testid="p03-overlay-svg">
          {holes.map((h) => box(h.bbox, color.h, h.feature_id))}
          {slots.map((s) => box(s.bbox, color.s, s.feature_id))}
          {dims.filter((dd) => dd.linked_path).map((dd) => box(dd.bbox, color.dl, dd.dim_id, "2 2", 1.1))}
          {dims.filter((dd) => !dd.linked_path).map((dd) => box(dd.bbox, color.df, dd.dim_id, "2 2", 1.1))}
          {bends.map((b, i) => box(b.bbox, color.b, `bend-${i}`, "5 3", 2))}
          {roll && box(roll.bbox, color.b, "rolling", "8 4", 2.2)}
        </svg>
      </div>
      <div className="text-[10px] text-muted-foreground mt-1" data-testid="p03-overlay-stats">
        {src.toUpperCase()} pagina {page}: {holes.length} fori · {slots.length} asole · {dims.filter((dd) => dd.linked_path).length} quote associate · {dims.filter((dd) => !dd.linked_path).length} non associate{src === "p03" && ` · ${bends.length} pieghe · calandratura: ${roll ? "sì" : "no"}`}
      </div>
    </div>
  );
};

/* ── pannello principale ──────────────────────────────────────── */
export const DeltaP03Panel = ({ caseId }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("campi");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/calibration/cases/${caseId}/p03-delta`, { timeout: 120000 });
      setData(r.data);
    } catch { setData({ missing: true }); }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setBusy(true);
    try {
      await api.post(`/calibration/p03/run`, {}, { timeout: 1800000 });
      toast.success("Esecuzione P0.3 congelata");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore P0.3"); }
    setBusy(false);
  };

  if (!data) return <div className="text-sm p-2" data-testid="p03-loading">Caricamento delta P0.3…</div>;
  if (data.missing) {
    return (
      <Card data-testid="p03-panel">
        <CardHeader className="py-3"><CardTitle className="text-sm">P0.3 — Delta post-fix (DEV_REGRESSION_CASE)</CardTitle></CardHeader>
        <CardContent>
          <p className="text-xs mb-2">Esecuzione P0.3 non ancora congelata. Il run usa il motore corretto in un processo fresco (cache_hit=false) sui PDF originali con hash verificati; P0.2 resta immutabile.</p>
          <Button size="sm" onClick={run} disabled={busy} data-testid="p03-run-btn">
            <GitCompareArrows size={13} className="mr-1" /> {busy ? "Esecuzione in corso…" : "Esegui P0.3 e genera il delta"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const TABS = [["campi", "Campi P0.2/P0.3/GT"], ["metriche", "TP·FP·FN & transizioni"], ["gate", "Gate & fasi produttive"], ["overlay", "Overlay P0.2/P0.3"]];
  return (
    <Card data-testid="p03-panel">
      <CardHeader className="py-3"><CardTitle className="text-sm">P0.3 — Delta verificabile: P0.2 congelato → P0.3 → ground truth</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        <Meta d={data} />
        <div className="flex gap-1 flex-wrap">
          {TABS.map(([k, l]) => (
            <Button key={k} size="sm" variant={tab === k ? "default" : "outline"} className="h-7 text-xs" onClick={() => setTab(k)} data-testid={`p03-tab-${k}`}>{l}</Button>
          ))}
        </div>
        {tab === "campi" && <FieldDelta rows={data.field_delta || []} />}
        {tab === "metriche" && <Transitions d={data} />}
        {tab === "gate" && <GateAndPhases d={data} />}
        {tab === "overlay" && <OverlayDelta d={data} caseId={caseId} />}
      </CardContent>
    </Card>
  );
};
